import type { StaticDatabase } from "@-/dev.improve.haniverse.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { alias } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import { asc, eq, inArray } from "@-/com.npmjs.drizzle-orm";

/** Maps a grapheme's glyph to its ordered decomposition components (left then right). */
export type DecompositionMap = ReadonlyMap<string, readonly string[]>;

/** Stable empty map handed back as the fallback before the query resolves. */
export const EMPTY_DECOMPOSITION_MAP: DecompositionMap = new Map();

/**
 * Reads `grapheme_component` rows joined to both grapheme glyphs and groups them into a
 * `glyph → components[]` map. With roots, reads only their transitive closure; without roots,
 * reads the full catalogue. Row insertion order in the static-DB builder preserves the
 * effective learning-card order: curator-pinned study decomposition, canonical IDS, then legacy
 * decomposition. Ordering by `grapheme_component.id` reproduces that order. Unsupported
 * placeholder components are filtered before the artifacts reach the assembler.
 *
 * Exported so the server-side test seed route can resolve the map directly
 * (without a QueryClient cache). Other callers should prefer
 * `useDecompositionMapQueryOptions` (this package's root entry) so the result is shared via the
 * QueryClient cache.
 */
export async function loadDecompositionMap(
  staticDb: StaticDatabase,
  roots?: readonly string[],
): Promise<DecompositionMap> {
  const compoundGrapheme = alias(staticSchema.grapheme, "compound_grapheme");
  const componentGrapheme = alias(staticSchema.grapheme, "component_grapheme");

  const read = (glyphs?: readonly string[]) =>
    staticDb(import.meta.location)
      .select({
        componentGlyph: componentGrapheme.glyph,
        glyph: compoundGrapheme.glyph,
      })
      .from(staticSchema.graphemeComponent)
      .innerJoin(
        compoundGrapheme,
        eq(staticSchema.graphemeComponent.graphemeId, compoundGrapheme.id),
      )
      .innerJoin(
        componentGrapheme,
        eq(staticSchema.graphemeComponent.componentGraphemeId, componentGrapheme.id),
      )
      .where(glyphs === undefined ? undefined : inArray(compoundGrapheme.glyph, [...glyphs]))
      .orderBy(asc(staticSchema.graphemeComponent.id));

  const map = new Map<string, string[]>();
  const append = (rows: { componentGlyph: string; glyph: string }[]) => {
    for (const row of rows) {
      let components = map.get(row.glyph);
      if (components === undefined) {
        components = [];
        map.set(row.glyph, components);
      }
      components.push(row.componentGlyph);
    }
  };
  if (roots === undefined) {
    append(await read());
    return map;
  }

  // A review queue needs only the closure of its due/exposed subjects. Reading the entire
  // catalogue makes an evicted scheduler dependency parse tens of thousands of remote
  // rows on the JS thread. Bound each read below D1's parameter cap,
  // including subsequent generations; visited roots also terminate cycles and missing glyphs.
  const visited = new Set<string>();
  const pending = [...roots];
  for (let offset = 0; offset < pending.length;) {
    const batch: string[] = [];
    while (offset < pending.length && batch.length < 64) {
      const glyph = pending[offset++];
      if (glyph !== undefined && !visited.has(glyph)) {
        visited.add(glyph);
        batch.push(glyph);
      }
    }
    if (batch.length === 0) {
      continue;
    }
    const rows = await read(batch);
    append(rows);
    for (const row of rows) {
      if (!visited.has(row.componentGlyph)) {
        pending.push(row.componentGlyph);
      }
    }
  }
  return map;
}
